import { useEffect, useRef, useState } from "react";
import "./styles.css";

type ObstacleType = "crate" | "enemy" | "shuriken";

interface Obstacle {
  id: number;
  type: ObstacleType;
  x: number;
  defeated?: boolean;
}

const PLAYER_X = 70;

/* NinjaCat floor */
const GROUND_Y = 165;

/* Boxes + enemies slightly higher */
const OBSTACLE_GROUND_Y = 175;

/* Shuriken height */
const SHURIKEN_Y = 215;

/* Jump */
const JUMP_POWER = 800;
const GRAVITY = 1900;

/* Speed */
const START_SPEED = 245;

/* Intro */
const INTRO_TIME = 2400;

/* Public folder helper for Netlify */
const asset = (file: string) =>
  `${process.env.PUBLIC_URL}/${file}`;

export default function App() {
  const gameRef = useRef<HTMLDivElement | null>(null);

  /* =========================================
     START SCREEN
     ========================================= */

  const [started, setStarted] = useState(false);

  const [playerY, setPlayerY] = useState(GROUND_Y);
  const [obstacles, setObstacles] = useState<Obstacle[]>([]);

  const [score, setScore] = useState(0);

  const [attacking, setAttacking] = useState(false);
  const [hit, setHit] = useState(false);
  const [gameOver, setGameOver] = useState(false);

  const [intro, setIntro] = useState(true);

  const [levelFlash, setLevelFlash] = useState(false);
  const [levelMessage, setLevelMessage] = useState(false);

  const [backgroundX, setBackgroundX] = useState(0);

  const playerYRef = useRef(GROUND_Y);
  const velocityRef = useRef(0);

  const obstaclesRef = useRef<Obstacle[]>([]);

  const attackingRef = useRef(false);
  const hitRef = useRef(false);

  const scoreRef = useRef(0);
  const speedRef = useRef(START_SPEED);

  const spawnTimerRef = useRef(0);

  const nextIdRef = useRef(1);

  const lastTimeRef = useRef<number | null>(null);

  const backgroundXRef = useRef(0);

  const jumping = playerY > GROUND_Y + 6;

  /* Level changes every 20 points */
  const level = Math.floor(score / 20) + 1;

  /* =========================================
     PRELOAD IMAGES
     ========================================= */

  useEffect(() => {
    const images = [
      "ninjacat-run.png",
      "ninjacat-jump.png",
      "ninjacat-attack.png",
      "ninjacat-hit.png",
      "crate.png",
      "enemy-ninja.png",
      "shuriken.png",
      "background.png",
    ];

    images.forEach((file) => {
      const image = new Image();
      image.src = asset(file);
    });
  }, []);

  /* =========================================
     INTRO
     ========================================= */

  useEffect(() => {
    /*
      IMPORTANT:
      Intro does not count down until
      START GAME has actually been pressed.
    */

    if (!started || !intro) return;

    const timer = window.setTimeout(() => {
      setIntro(false);
    }, INTRO_TIME);

    return () => {
      window.clearTimeout(timer);
    };
  }, [started, intro]);

  /* =========================================
     LEVEL UP FLASH
     ========================================= */

  useEffect(() => {
    if (score === 0) return;

    if (score % 20 === 0) {
      setLevelFlash(true);
      setLevelMessage(true);

      const timer = window.setTimeout(() => {
        setLevelFlash(false);
        setLevelMessage(false);
      }, 900);

      return () => {
        window.clearTimeout(timer);
      };
    }
  }, [score]);

  /* =========================================
     START GAME
     ========================================= */

  const startGame = () => {
    /*
      Reset timing so there is no giant
      frame jump when START is pressed.
    */

    lastTimeRef.current = null;

    setStarted(true);
    setIntro(true);
  };

  /* =========================================
     JUMP
     ========================================= */

  const jump = () => {
    /*
      No jumping while the start screen
      is still showing.
    */

    if (!started) return;

    if (gameOver) {
      restart();
      return;
    }

    if (hitRef.current) return;

    if (playerYRef.current <= GROUND_Y + 6) {
      velocityRef.current = JUMP_POWER;
    }
  };

  /* =========================================
     ATTACK
     ========================================= */

  const attack = () => {
    /*
      No attacking before START GAME.
    */

    if (!started) return;

    if (
      gameOver ||
      hitRef.current ||
      attackingRef.current
    ) {
      return;
    }

    attackingRef.current = true;
    setAttacking(true);

    window.setTimeout(() => {
      attackingRef.current = false;
      setAttacking(false);
    }, 320);
  };

  /* =========================================
     HIT
     ========================================= */

  const triggerHit = () => {
    if (hitRef.current || gameOver) return;

    hitRef.current = true;
    setHit(true);

    window.setTimeout(() => {
      setGameOver(true);
    }, 450);
  };

  /* =========================================
     RESTART
     ========================================= */

  const restart = () => {
    playerYRef.current = GROUND_Y;
    velocityRef.current = 0;

    obstaclesRef.current = [];

    scoreRef.current = 0;
    speedRef.current = START_SPEED;

    spawnTimerRef.current = 0;

    backgroundXRef.current = 0;

    lastTimeRef.current = null;

    attackingRef.current = false;
    hitRef.current = false;

    setPlayerY(GROUND_Y);
    setObstacles([]);

    setScore(0);

    setBackgroundX(0);

    setAttacking(false);
    setHit(false);
    setGameOver(false);

    setLevelFlash(false);
    setLevelMessage(false);

    setIntro(true);
  };

  /* =========================================
     KEYBOARD
     ========================================= */

  useEffect(() => {
    const keyDown = (event: KeyboardEvent) => {
      /*
        Keyboard does nothing while
        start screen is open.
      */

      if (!started) return;

      if (
        event.code === "Space" ||
        event.code === "ArrowUp"
      ) {
        event.preventDefault();
        jump();
      }

      if (event.code === "KeyX") {
        event.preventDefault();
        attack();
      }
    };

    window.addEventListener("keydown", keyDown);

    return () => {
      window.removeEventListener("keydown", keyDown);
    };
  });

  /* =========================================
     MAIN GAME LOOP
     ========================================= */

  useEffect(() => {
    let frameId = 0;

    const loop = (time: number) => {
      if (lastTimeRef.current === null) {
        lastTimeRef.current = time;
      }

      const dt = Math.min(
        (time - lastTimeRef.current) / 1000,
        0.033
      );

      lastTimeRef.current = time;

      /*
        NOTHING in the game world moves
        until START GAME has been pressed.
      */

      if (
        started &&
        !gameOver &&
        !hitRef.current
      ) {
        const gameWidth =
          gameRef.current?.clientWidth ||
          window.innerWidth;

        /* BACKGROUND */

        backgroundXRef.current +=
          speedRef.current * 0.32 * dt;

        if (backgroundXRef.current >= gameWidth) {
          backgroundXRef.current -= gameWidth;
        }

        setBackgroundX(backgroundXRef.current);

        /* PLAYER PHYSICS */

        velocityRef.current -= GRAVITY * dt;

        playerYRef.current +=
          velocityRef.current * dt;

        if (playerYRef.current <= GROUND_Y) {
          playerYRef.current = GROUND_Y;
          velocityRef.current = 0;
        }

        setPlayerY(playerYRef.current);

        /* GAME ONLY STARTS AFTER INTRO */

        if (!intro) {
          /* SPAWN */

          spawnTimerRef.current += dt;

          if (spawnTimerRef.current >= 1.7) {
            spawnTimerRef.current = 0;

            const choices: ObstacleType[] = [
              "crate",
              "enemy",
              "shuriken",
            ];

            const type =
              choices[
                Math.floor(
                  Math.random() * choices.length
                )
              ];

            obstaclesRef.current = [
              ...obstaclesRef.current,
              {
                id: nextIdRef.current++,
                type,
                x: gameWidth + 80,
              },
            ];
          }

          /* MOVE */

          let nextObstacles =
            obstaclesRef.current
              .map((obstacle) => ({
                ...obstacle,
                x:
                  obstacle.x -
                  speedRef.current * dt,
              }))
              .filter(
                (obstacle) =>
                  obstacle.x > -130
              );

          /* PLAYER HITBOX */

          const playerLeft = PLAYER_X + 27;
          const playerRight = PLAYER_X + 78;

          const playerBottom =
            playerYRef.current + 14;

          const playerTop =
            playerYRef.current + 78;

          /* COLLISIONS */

          nextObstacles = nextObstacles.map(
            (obstacle) => {
              if (obstacle.defeated) {
                return obstacle;
              }

              let obstacleWidth = 66;
              let obstacleHeight = 66;

              let obstacleBottom =
                OBSTACLE_GROUND_Y;

              if (obstacle.type === "enemy") {
                obstacleWidth = 88;
                obstacleHeight = 88;
              }

              if (obstacle.type === "shuriken") {
                obstacleWidth = 44;
                obstacleHeight = 44;
                obstacleBottom = SHURIKEN_Y;
              }

              const obstacleLeft =
                obstacle.x +
                obstacleWidth * 0.3;

              const obstacleRight =
                obstacle.x +
                obstacleWidth * 0.7;

              const obstacleHitBottom =
                obstacleBottom +
                obstacleHeight * 0.22;

              const obstacleTop =
                obstacleBottom +
                obstacleHeight * 0.78;

              const touchingX =
                playerRight > obstacleLeft &&
                playerLeft < obstacleRight;

              const touchingY =
                playerTop >
                  obstacleHitBottom &&
                playerBottom < obstacleTop;

              if (!touchingX || !touchingY) {
                return obstacle;
              }

              /* ENEMY ATTACK */

              if (
                obstacle.type === "enemy" &&
                attackingRef.current
              ) {
                return {
                  ...obstacle,
                  defeated: true,
                };
              }

              /* SHURIKEN ATTACK */

              if (
                obstacle.type === "shuriken" &&
                attackingRef.current
              ) {
                return {
                  ...obstacle,
                  defeated: true,
                };
              }

              triggerHit();

              return obstacle;
            }
          );

          obstaclesRef.current = nextObstacles;

          setObstacles(nextObstacles);

          /* SCORE = ABOUT 1 PER SECOND */

          scoreRef.current += dt;

          const nextScore =
            Math.floor(scoreRef.current);

          setScore(nextScore);

          /* SPEED INCREASE EVERY LEVEL */

          speedRef.current =
            START_SPEED +
            Math.floor(nextScore / 20) * 50;
        }
      }

      frameId =
        requestAnimationFrame(loop);
    };

    frameId =
      requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [started, gameOver, intro]);

  /* =========================================
     PLAYER IMAGE
     ========================================= */

  const getPlayerImage = () => {
    if (hit) {
      return asset("ninjacat-hit.png");
    }

    if (attacking) {
      return asset("ninjacat-attack.png");
    }

    if (jumping) {
      return asset("ninjacat-jump.png");
    }

    return asset("ninjacat-run.png");
  };

  /* =========================================
     OBSTACLE IMAGE
     ========================================= */

  const obstacleImage = (
    type: ObstacleType
  ) => {
    if (type === "crate") {
      return asset("crate.png");
    }

    if (type === "enemy") {
      return asset("enemy-ninja.png");
    }

    return asset("shuriken.png");
  };

  /* =========================================
     RENDER
     ========================================= */

  return (
    <div
      className={`game ${
        levelFlash
          ? "level-flash"
          : ""
      }`}
      ref={gameRef}
    >
      {/* BACKGROUND */}

      <div
        className="world-panel"
        style={{
          left: `${-backgroundX}px`,
          backgroundImage: `url("${asset(
            "background.png"
          )}")`,
        }}
      />

      <div
        className="world-panel"
        style={{
          left: `calc(100% - ${backgroundX}px)`,
          backgroundImage: `url("${asset(
            "background.png"
          )}")`,
        }}
      />

      {/* =====================================
          START SCREEN
          ===================================== */}

      {!started && (
        <div className="start-screen">
          <div className="start-panel">
            <div className="start-title">
              NINJACAT RUN
            </div>

            <div className="start-subtitle">
              EMBRACE THE SHADOWS
            </div>

            <img
              src={asset("ninjacat-run.png")}
              alt="NinjaCat"
              className="start-ninjacat"
            />

            <div className="start-controls-text">
              JUMP • ATTACK • SURVIVE
            </div>

            <button
              className="start-button"
              onClick={startGame}
            >
              START GAME
            </button>

            <div className="start-hint">
              SPACE = JUMP
              &nbsp; • &nbsp;
              X = ATTACK
            </div>
          </div>
        </div>
      )}

      {/* HUD */}

      <div className="hud">
        <span>Score: {score}</span>
        <span>Level: {level}</span>
      </div>

      {/* INTRO */}

      {started && intro && (
        <div className="intro-title">
          <div className="intro-main">
            NINJACAT RUN
          </div>

          <div className="intro-sub">
            ENTER THE SHADOWS
          </div>
        </div>
      )}

      {/* LEVEL UP */}

      {levelMessage && (
        <div className="level-up-message">
          LEVEL {level}
        </div>
      )}

      {/* PLAYER */}

      <img
        src={getPlayerImage()}
        alt="NinjaCat"
        className={[
          "player",
          jumping
            ? "player-jumping"
            : "",
          attacking
            ? "player-attacking"
            : "",
          hit
            ? "player-hit"
            : "",
        ].join(" ")}
        style={{
          left: PLAYER_X,
          bottom: playerY,
        }}
      />

      {/* OBSTACLES */}

      {obstacles.map((obstacle) => (
        <img
          key={obstacle.id}
          src={obstacleImage(
            obstacle.type
          )}
          alt={obstacle.type}
          className={`obstacle ${
            obstacle.type
          } ${
            obstacle.defeated
              ? "defeated"
              : ""
          }`}
          style={{
            left: obstacle.x,

            bottom:
              obstacle.type ===
              "shuriken"
                ? SHURIKEN_Y
                : OBSTACLE_GROUND_Y,
          }}
        />
      ))}

      {/* GAME OVER */}

      {gameOver && (
        <div className="game-over">
          <h1>NINJACAT DOWN</h1>

          <p>Score: {score}</p>

          <p>Level: {level}</p>

          <button onClick={restart}>
            TRY AGAIN
          </button>
        </div>
      )}

      {/* CONTROLS */}

      {started && !gameOver && (
        <div className="controls">
          <button
            className="jump"
            onPointerDownCapture={(
              event
            ) => {
              event.preventDefault();
              jump();
            }}
          >
            JUMP
          </button>

          <button
            className="attack"
            onPointerDownCapture={(
              event
            ) => {
              event.preventDefault();
              attack();
            }}
            onTouchStart={(event) => {
              event.preventDefault();
              attack();
            }}
          >
            ATTACK
          </button>
        </div>
      )}

      {/* KEYBOARD HELP */}

      {started && (
        <div className="keyboard-help">
          SPACE = JUMP
          &nbsp; | &nbsp;
          X = ATTACK
        </div>
      )}
    </div>
  );
}
